import { useScrollReveal, useScrollRevealGroup } from '@/hooks/useScrollReveal';

const stages = [
  {
    icon: 'ri-chat-1-line',
    title: 'Первое сообщение',
    desc: 'ИИ пишет клиенту, представляется и уточняет актуальность интереса.',
  },
  {
    icon: 'ri-message-2-line',
    title: 'Диалог и ответы',
    desc: 'Отвечает на частые вопросы, рассказывает о продукте и помогает клиенту выбрать подходящий вариант.',
  },
  {
    icon: 'ri-notification-3-line',
    title: 'Напоминание',
    desc: 'Если человек не ответил, ИИ автоматически отправляет аккуратное повторное сообщение через согласованный интервал.',
  },
  {
    icon: 'ri-filter-3-line',
    title: 'Квалификация',
    desc: 'Уточняет бюджет, сроки, географию, услугу и другие важные параметры.',
  },
  {
    icon: 'ri-user-shared-line',
    title: 'Передача менеджеру',
    desc: 'Когда клиент готов к дальнейшему шагу, ИИ передаёт заявку вместе со всей перепиской вашему менеджеру или в CRM.',
  },
];

const flowSteps = [
  'Контакт с согласием',
  'Первое сообщение ИИ',
  'Диалог',
  'Напоминание',
  'Квалификация',
  'Отдел продаж',
];

export default function LTKAIDialog() {
  const { ref: labelRef, revealed: labelRevealed } = useScrollReveal();
  const { ref: titleRef, revealed: titleRevealed } = useScrollReveal({ delay: 100 });
  const { containerRef: cardsRef, revealedCount: cardsRevealed } = useScrollRevealGroup<HTMLDivElement>(5);
  const { ref: flowRef, revealed: flowRevealed } = useScrollReveal({ delay: 200 });

  return (
    <section className="py-20 md:py-28 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="text-center mb-12 md:mb-16">
          <span ref={labelRef} className={`text-[12px] font-semibold uppercase tracking-[0.18em] text-primary-600 reveal-fade-up ${labelRevealed ? 'revealed' : ''}`}>
            ИИ в работе
          </span>
          <h2 ref={titleRef} className={`mt-4 font-display font-extrabold text-[34px] md:text-[46px] leading-[1.1] tracking-tightest text-foreground-950 reveal-fade-up ${titleRevealed ? 'revealed' : ''}`}>
            ИИ начинает диалог, пока клиент ещё заинтересован
          </h2>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {stages.slice(0, 3).map((stage, i) => (
            <div
              key={stage.title}
              className={`rounded-2xl bg-background-100 border border-background-200/70 p-6 md:p-7 hover:border-primary-500/40 transition-all duration-300 group reveal-fade-up ${i < cardsRevealed ? 'revealed' : ''}`}
              style={i < cardsRevealed ? { transitionDelay: `${i * 120}ms` } : undefined}
            >
              <span className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 mb-4 group-hover:bg-primary-200 transition-colors">
                <i className={`${stage.icon} text-lg`}></i>
              </span>
              <h3 className="font-display font-bold text-[17px] text-foreground-950 mb-2">{stage.title}</h3>
              <p className="text-[14.5px] text-foreground-500 leading-relaxed">{stage.desc}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto mt-5">
          {stages.slice(3).map((stage, i) => (
            <div
              key={stage.title}
              className={`rounded-2xl bg-background-100 border border-background-200/70 p-6 md:p-7 hover:border-primary-500/40 transition-all duration-300 group reveal-fade-up ${i + 3 < cardsRevealed ? 'revealed' : ''}`}
              style={i + 3 < cardsRevealed ? { transitionDelay: `${(i + 3) * 120}ms` } : undefined}
            >
              <span className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 mb-4 group-hover:bg-primary-200 transition-colors">
                <i className={`${stage.icon} text-lg`}></i>
              </span>
              <h3 className="font-display font-bold text-[17px] text-foreground-950 mb-2">{stage.title}</h3>
              <p className="text-[14.5px] text-foreground-500 leading-relaxed">{stage.desc}</p>
            </div>
          ))}
        </div>

        <div ref={flowRef} className={`mt-12 reveal-fade-up ${flowRevealed ? 'revealed' : ''}`}>
          <div className="max-w-4xl mx-auto bg-background-100 rounded-2xl border border-background-200/70 p-6 md:p-8">
            <div className="text-[12px] font-semibold uppercase tracking-wider text-foreground-400 mb-5 text-center">Схема работы</div>
            <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
              {flowSteps.map((step, i) => (
                <div key={step} className="flex items-center gap-2 md:gap-3">
                  <div className={`px-4 py-2 rounded-full text-[13px] md:text-[14px] font-medium whitespace-nowrap ${
                    i === flowSteps.length - 1
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 border border-background-200 text-foreground-700'
                  }`}>
                    {step}
                  </div>
                  {i < flowSteps.length - 1 && (
                    <span className="text-foreground-300">
                      <i className="ri-arrow-right-line text-sm"></i>
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}